/**
 * The API client.
 *
 * Everything goes through one function so that error shape, credentials and
 * the "you were signed out" case are handled once. The session lives in an
 * httpOnly cookie, so `credentials: "include"` is the whole of the auth code
 * here — there is no token for this bundle to hold, lose or leak.
 */

export interface ApiError {
  code: string;
  message: string;
}

export class RequestFailed extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message);
    this.name = "RequestFailed";
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers:
      init.body === undefined ? init.headers : { "Content-Type": "application/json", ...init.headers },
  });

  if (response.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }

  if (!response.ok) {
    const error = (body as { error?: ApiError } | undefined)?.error;
    throw new RequestFailed(
      response.status,
      error ?? { code: "unknown", message: "Something went wrong." },
    );
  }
  return body as T;
}

const post = <T,>(path: string, body?: unknown): Promise<T> =>
  call<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

// ─── types mirroring the API's replies ───────────────────────────────────────

/**
 * What the API sends for a player. `RosterPlayer` below is the whole of it;
 * this is the narrower view the attendance sheet reads, which is the same
 * payload with the fields it does not use left out of the type.
 *
 * The server picks these explicitly (`playerForClient` in packages/api) — the
 * domain object also carries an email address and a full name, and neither
 * ever reaches the wire. There is a test asserting the exact key set, so if
 * something is missing here it is missing on purpose.
 */
export interface Player {
  id: string;
  displayName: string;
  isGuest: boolean;
}

export interface Me {
  id: string;
  displayName: string;
  role: "member" | "admin";
  clubId: string;
}

export interface RosterPlayer {
  id: string;
  displayName: string;
  username: string | null;
  role: "member" | "admin";
  status: "active" | "inactive" | "guest";
  isGuest: boolean;
}

export interface SessionListItem extends Session {
  gameCount: number;
  attendeeCount: number;
  isToday: boolean;
  isUpcoming: boolean;
}

export interface SessionListing {
  /** The club's today, in the club's timezone. Never the device's. */
  today: string;
  upcoming: SessionListItem[];
  today_: SessionListItem[];
  recent: SessionListItem[];
  hasMore: boolean;
}

export interface Session {
  id: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  venue: string | null;
  courtCount: number;
  targetScore: number;
  capacity: number;
  status: "scheduled" | "open" | "committed";
}

export interface QueueEntry {
  playerId: string;
  displayName: string;
  isGuest: boolean;
  position: number;
  isOutOfRotation: boolean;
  gamesPlayed: number;
  gamesSat: number;
}

export interface Attendee {
  playerId: string;
  displayName: string;
  isGuest: boolean;
  gamesPlayed: number;
}

/** What the attendance endpoint returns for both marking present and adding a guest. */
export interface MarkPresentResult {
  attendee: Attendee;
  /** True when the call did nothing because they were already present. */
  alreadyPresent: boolean;
  /** ATTEND-03 — the roster now exceeds capacity. Flagged, never blocked. */
  overCapacity: boolean;
}

export type Matchup =
  | { available: true; sideA: string[]; sideB: string[]; resting: string[]; benchIsEmpty: boolean }
  | { available: false; reason: string; message: string };

export interface ScoreOption {
  winnerScore: number;
  loserScore: number;
  group: "normal" | "deuce";
}

export interface CourtCard {
  courtNumber: number;
  lastSideA: string[];
  lastSideB: string[];
  lastScore: { a: number; b: number } | null;
  gamesOnThisCourt: number;
}

export interface Entry {
  targetScore: number;
  /** BR-SCORE-1a — the cap in force, which a tournament can move. */
  capScore: number;
  alternateTargets: number[];
  courtCount: number;
  matchup: Matchup;
  /** FR-ENTRY-13, AC-34 - empty on a single-court session. */
  courts: CourtCard[];
  singlesMatchup: Matchup;
  scoreOptions: ScoreOption[];
  /** BR-SCORE-0 — one grid per target the toggle offers. */
  scoreOptionsByTarget: Record<string, ScoreOption[]>;
}

export interface Game {
  id: string;
  sideA: string[];
  sideB: string[];
  sideAScore: number;
  sideBScore: number;
  winnerSide: "A" | "B";
  targetScore: number;
  sequenceNo: number;
}


// ─── commit and resolution ───────────────────────────────────────────────────

export interface Blocker {
  kind: "open_duplicates" | "open_conflicts" | "unsynced_ops" | "no_games";
  count: number;
  message: string;
  resolvableHere: boolean;
}

export interface ChangeLogEntry {
  gameId: string | null;
  action: string;
  actorName: string | null;
  at: string;
  classification: string | null;
}

export interface DuplicateGameSummary {
  id: string;
  sideA: string[];
  sideB: string[];
  sideAScore: number;
  sideBScore: number;
  targetScore: number;
  playedAt: string;
  enteredByName: string | null;
  counts: boolean;
}

export interface CandidateDetail {
  id: string;
  reason: string;
  prompt: string;
  gameA: DuplicateGameSummary;
  gameB: DuplicateGameSummary;
}

export interface ConflictVersion {
  sideAScore: number;
  sideBScore: number;
  targetScore: number;
  attribution: string;
  savedAt: string | null;
  deleted: boolean;
}

export interface ConflictDetail {
  id: string;
  gameId: string;
  serverVersion: number;
  mine: ConflictVersion;
  theirs: ConflictVersion;
}

export interface PlayerStanding {
  playerId: string;
  displayName: string;
  isGuest: boolean;
  rank: number;
  shared: boolean;
  games: number;
  wins: number;
  losses: number;
  winPct: number | null;
  pointsWon: number;
  pointsLost: number;
  pointDiff: number;
  dominance: number | null;
  normalisedDominance: number | null;
  matchPoints: number;
  bonus: number;
  penalties: number;
  pointsAvg: number | null;
}

/** BR-PAIR-1, BR-PAIR-2 — one partnership, both orders. */
export interface PairStanding {
  pairKey: string;
  playerIds: [string, string];
  displayNames: [string, string];
  label: string;
  meetsMinimum: boolean;
  games: number;
  wins: number;
  losses: number;
  winPct: number | null;
  pointsWon: number;
  pointsLost: number;
  pointDiff: number;
  dominance: number | null;
  normalisedDominance: number | null;
  matchPoints: number;
  bonus: number;
  penalties: number;
  pointsAvg: number | null;
}

export interface PairReport {
  sessionId: string;
  provisional: boolean;
  minimumGames: number;
  rows: PairStanding[];
}

export interface StandingsReport {
  sessionId: string;
  gameType: "doubles" | "singles";
  provisional: boolean;
  rows: PlayerStanding[];
  leaders: string[];
  identityHolds: boolean;
}

export interface SessionSummary {
  sessionId: string;
  date: string;
  venue: string | null;
  gameCount: number;
  provisional: boolean;
  standings: PlayerStanding[];
  pairs: PairStanding[];
  award: {
    winners: Array<{ playerId: string; displayName: string; matchPoints: number }>;
    runnersUp: Array<{ playerId: string; displayName: string; matchPoints: number }>;
  } | null;
  /** FR-TOUR-5 — null for an ordinary session. */
  tournament: TournamentView | null;
  editedCount: number;
  text: string;
}

export interface LeaderboardRow extends PlayerStanding {
  qualified: boolean;
  qualificationProgress: string | null;
}

export interface Leaderboard {
  gameType: "doubles" | "singles";
  qualifyingGames: number;
  ranked: LeaderboardRow[];
  notYetQualified: LeaderboardRow[];
}

export interface CounterpartRecord extends PlayerStanding {
  meetsMinimum: boolean;
  minimumGames: number;
}

export interface Insight {
  kind: string;
  text: string;
  sample: number;
}

export interface PlayerProfileReport {
  playerId: string;
  displayName: string;
  isGuest: boolean;
  gameType: "doubles" | "singles";
  allTime: PlayerStanding;
  recent: PlayerStanding;
  recentSessions: number;
  sessionsPlayed: number;
  partners: CounterpartRecord[];
  opponents: CounterpartRecord[];
  insights: Insight[];
}

export interface PairProfileReport {
  pairKey: string;
  label: string;
  playerIds: [string, string];
  together: PlayerStanding;
  meetsMinimum: boolean;
  minimumGames: number;
  against: CounterpartRecord[];
}

/** FR-ADMIN-11 — what needs an admin, and how the backup is doing. */
export interface AttentionItem {
  kind:
    | "duplicate"
    | "conflict"
    | "post_commit_arrival"
    | "uncommitted_session"
    | "same_day_session"
    | "backup";
  severity: "problem" | "warning";
  title: string;
  detail: string;
  since: string | null;
  sessionId: string | null;
}

export interface BackupHealth {
  state: "ok" | "stale" | "failed" | "missing" | "unknown";
  lastVerified: string | null;
  rows: number | null;
  offsite: "ok" | "failed" | "not-configured" | null;
  message: string;
}

export interface AttentionReport {
  items: AttentionItem[];
  count: number;
  backup: BackupHealth;
}

export interface BreakdownReport {
  gameType: "doubles" | "singles";
  months: Array<{
    month: string;
    rows: Array<PlayerStanding & { month: string; sessionsPlayed: number }>;
  }>;
}

export interface AuditEntry {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  actorName: string | null;
  reason: string | null;
  at: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  summary: string;
}

export interface Preflight {
  sessionId: string;
  status: "scheduled" | "open" | "committed";
  gameCount: number;
  played: Attendee[];
  presentButDidNotPlay: Attendee[];
  blockers: Blocker[];
  duplicates: CandidateDetail[];
  conflicts: ConflictDetail[];
  editedCount: number;
  deletedCount: number;
  changeLog: ChangeLogEntry[];
  lateArrivals: Array<{ gameId: string; enteredByName: string | null; playedAt: string }>;
  canCommit: boolean;
}

export interface CommitResult {
  committedAt: string;
  gamesCounted: number;
  resultsGenerated: number;
  changed: boolean;
}

// ─── tournaments (FR-TOUR-1, FR-TOUR-4, FR-TOUR-5) ───────────────────────────

export interface TournamentMember {
  code: string;
  slot: number;
  playerId: string;
  displayName: string;
  isGuest: boolean;
}

export interface TournamentTeam {
  id: string;
  code: string;
  name: string;
  sortOrder: number;
  members: TournamentMember[];
}

export interface FixtureSidePlayer {
  code: string;
  playerId: string;
  displayName: string;
}

export interface FixtureResult {
  gameId: string;
  sideAScore: number;
  sideBScore: number;
  winnerSide: "A" | "B";
  sideAPlayerIds: string[];
  sideBPlayerIds: string[];
  playedAt: string;
}

export interface TournamentFixture {
  id: string;
  roundNo: number;
  courtNumber: number;
  sequenceNo: number;
  sideATeamCode: string;
  sideBTeamCode: string;
  sideA: FixtureSidePlayer[];
  sideB: FixtureSidePlayer[];
  result: FixtureResult | null;
}

/** BR-TOUR-1, BR-TOUR-2 — the figures every tournament table shares. */
export interface TournamentRow {
  played: number;
  wins: number;
  losses: number;
  points: number;
  pointsWon: number;
  pointsLost: number;
  pointDiff: number;
  rank: number;
  shared: boolean;
}

export interface TeamStanding extends TournamentRow {
  teamId: string;
  code: string;
  name: string;
}

export interface TournamentPairStanding extends TournamentRow {
  pairCode: string;
  teamCode: string;
  playerIds: string[];
  label: string;
}

export interface TournamentPlayerStanding extends TournamentRow {
  playerId: string;
  code: string;
  teamCode: string;
  displayName: string;
}

export interface TournamentView {
  id: string;
  sessionId: string;
  name: string;
  format: string;
  targetScore: number;
  capScore: number;
  deuceEnabled: boolean;
  pointsPerWin: number;
  countsTowardAlltime: boolean;
  scheduleTemplateId: string | null;
  courtCount: number;
  roundCount: number;
  breakAfterRounds: number[];
  teams: TournamentTeam[];
  fixtures: TournamentFixture[];
  currentRoundNo: number | null;
  playedCount: number;
  fixtureCount: number;
  provisional: boolean;
  scoreOptions: ScoreOption[];
  standings: {
    teams: TeamStanding[];
    pairs: TournamentPairStanding[];
    players: TournamentPlayerStanding[];
    leadingTeamCodes: string[];
  };
}

export interface SessionView {
  session: Session;
  attendees: Attendee[];
  queue: QueueEntry[];
  entry: Entry;
  games: Game[];
  standings: StandingsReport;
  pairs: PairReport;
  /** FR-TOUR-4 — null for an ordinary session, which is nearly all of them. */
  tournament: TournamentView | null;
}

// ─── calls ───────────────────────────────────────────────────────────────────

export const api = {
  me: () => call<{ player: Me }>("/api/auth/me").then((r) => r.player),
  login: (username: string, password: string) =>
    post<{ player: Me }>("/api/auth/login", { username, password }).then((r) => r.player),
  logout: () => post<void>("/api/auth/logout"),

  players: () => call<{ players: Player[] }>("/api/players").then((r) => r.players),

  /** `all` includes guests and deactivated players — the admin roster. */
  roster: (all = false) =>
    call<{ players: RosterPlayer[] }>(`/api/players${all ? "?all=true" : ""}`).then(
      (r) => r.players,
    ),

  /** Same row, relabelled — every game they played comes with them. */
  convertGuest: (
    playerId: string,
    body: { displayName?: string; credentials?: { username: string } } = {},
  ) =>
    post<{ player: RosterPlayer; temporaryPassword?: string }>(
      `/api/players/${playerId}/convert`,
      body,
    ),

  /** Irreversible. `keepId` survives; `mergeId` becomes a redirect to it. */
  mergePlayers: (keepId: string, mergeId: string, reason: string) =>
    post<{ result: { gamesReassigned: number; resultsReassigned: number; attendanceReassigned: number; attendanceDeduplicated: number } }>(
      `/api/players/${keepId}/merge`,
      { mergeId, reason },
    ).then((r) => r.result),

  deleteGuest: (playerId: string, reason: string) =>
    call<{ deletedPlayerId: string; displayName: string }>(`/api/players/${playerId}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    }),

  /**
   * Only the fields sent are changed. Send `null` to clear one; leave it out
   * to keep it. The server distinguishes the two, which is why this takes a
   * partial rather than the whole session.
   */
  updateSession: (
    sessionId: string,
    patch: {
      date?: string;
      startTime?: string | null;
      endTime?: string | null;
      venue?: string | null;
      courtCount?: number;
      targetScore?: number;
      reason?: string;
    },
  ) =>
    call<{ session: Session }>(`/api/sessions/${sessionId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }).then((r) => r.session),

  discardSession: (sessionId: string, reason: string) =>
    call<{ discardedId: string }>(`/api/sessions/${sessionId}`, {
      method: "DELETE",
      body: JSON.stringify({ reason }),
    }),

  addPlayer: (displayName: string) =>
    post<{ player: RosterPlayer }>("/api/players", { displayName }).then((r) => r.player),

  /** Returns the temporary password once, to be read out and then changed. */
  grantLogin: (playerId: string, username: string, role: "member" | "admin" = "member") =>
    post<{ player: RosterPlayer; temporaryPassword: string }>(
      `/api/players/${playerId}/credentials`,
      { username, role },
    ),

  /** On success every device is signed out, including this one. */
  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ ok: true; signedOut: true }>("/api/auth/password", { currentPassword, newPassword }),

  /** Every session the picker needs, partitioned on the server. */
  sessions: (recent?: number) =>
    call<SessionListing>(`/api/sessions${recent ? `?recent=${String(recent)}` : ""}`),

  today: () =>
    call<{
      sessions: Session[];
      defaults: {
        date: string;
        startTime: string | null;
        endTime: string | null;
        venue: string | null;
        courtCount: number;
        targetScore: number;
      };
    }>("/api/sessions/today"),
  createSession: (body: {
    date?: string;
    courtCount?: number;
    venue?: string | null;
    targetScore?: number;
  }) =>
    post<{ session: Session }>("/api/sessions", body).then((r) => r.session),

  session: (id: string) => call<SessionView>(`/api/sessions/${id}`),

  /*
   * FR-ALLOC-12a — both of these return the attendee the server recorded, and
   * the caller confirms using *that* name rather than the one it sent. The
   * difference matters for a guest: the row it names is the row that exists.
   */
  markPresent: (sessionId: string, playerId: string) =>
    post<MarkPresentResult>(`/api/sessions/${sessionId}/attendance`, { playerId }),
  addGuest: (sessionId: string, displayName: string) =>
    post<MarkPresentResult>(`/api/sessions/${sessionId}/attendance`, { displayName }),
  unmark: (sessionId: string, playerId: string) =>
    call<void>(`/api/sessions/${sessionId}/attendance/${playerId}`, { method: "DELETE" }),

  /**
   * FR-ENTRY-15 — the whole order, not a move. The server validates that the
   * set matches the session's attendees exactly, so a client that has drifted
   * cannot quietly drop somebody out of the rotation by reordering it.
   */
  reorderQueue: (sessionId: string, order: string[], reason?: string) =>
    call<unknown>(`/api/sessions/${sessionId}/rotation/order`, {
      method: "PUT",
      body: JSON.stringify({ order, reason }),
    }),

  takeOut: (sessionId: string, playerId: string) =>
    post<unknown>(`/api/sessions/${sessionId}/rotation/out`, { playerId }),
  bringBack: (sessionId: string, playerId: string) =>
    post<unknown>(`/api/sessions/${sessionId}/rotation/return`, { playerId }),

  undoGame: (gameId: string) => call<void>(`/api/games/${gameId}`, { method: "DELETE" }),

  /**
   * FR-ENTRY-10 — correct a game that was entered wrongly.
   *
   * Every edit is audited and classified server-side, so a reason is worth
   * sending even when the API would accept none.
   */
  editGame: (
    gameId: string,
    patch: {
      sideA?: string[];
      sideB?: string[];
      sideAScore?: number;
      sideBScore?: number;
      targetScore?: number;
      reason?: string;
    },
  ) =>
    call<{ game: Game }>(`/api/games/${gameId}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /**
   * FR-TOUR-1 — create a tournament and the session that carries its games.
   *
   * The draw is sent as code-to-player, so the server builds the fixtures from
   * the named schedule rather than trusting 40 rows from a phone.
   */
  createTournament: (body: {
    name: string;
    scheduleTemplateId: string;
    targetScore: number;
    capScore: number;
    pointsPerWin?: number;
    date?: string;
    startTime?: string | null;
    venue?: string | null;
    teams: Array<{
      code: string;
      name: string;
      members: Array<{ code: string; playerId: string }>;
    }>;
  }) =>
    post<{ tournament: { id: string; sessionId: string } }>("/api/tournaments", body).then(
      (r) => r.tournament,
    ),

  /** FR-TOUR-4a — a no-show's place goes to somebody else. */
  substituteTournamentMember: (
    tournamentId: string,
    code: string,
    playerId: string,
    reason?: string,
  ) =>
    call<{ tournament: TournamentView }>(`/api/tournaments/${tournamentId}/members/${code}`, {
      method: "PUT",
      body: JSON.stringify({ playerId, reason }),
    }).then((r) => r.tournament),

  /** FR-STAT-10 — whether these games join the club's all-time figures. */
  setTournamentCountsTowardAlltime: (tournamentId: string, countsTowardAlltime: boolean) =>
    call<{ tournament: TournamentView }>(`/api/tournaments/${tournamentId}`, {
      method: "PATCH",
      body: JSON.stringify({ countsTowardAlltime }),
    }).then((r) => r.tournament),

  standings: (sessionId: string, gameType: "doubles" | "singles") =>
    call<StandingsReport>(`/api/sessions/${sessionId}/standings?gameType=${gameType}`),

  summary: (sessionId: string) => call<SessionSummary>(`/api/sessions/${sessionId}/summary`),

  leaderboard: (
    params: {
      gameType?: string;
      days?: number;
      sessions?: number;
      /** `2026-08`. */
      month?: string;
      year?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.gameType) q.set("gameType", params.gameType);
    if (params.days) q.set("days", String(params.days));
    if (params.sessions) q.set("sessions", String(params.sessions));
    if (params.month) q.set("month", params.month);
    if (params.year) q.set("year", String(params.year));
    return call<Leaderboard>(`/api/leaderboard?${q.toString()}`);
  },

  /** FR-STAT-3 — how each player's figures moved, month by month. */
  breakdown: (gameType: "doubles" | "singles" = "doubles", months = 12) =>
    call<BreakdownReport>(
      `/api/leaderboard/breakdown?gameType=${gameType}&months=${String(months)}`,
    ),

  playerProfile: (playerId: string, gameType: "doubles" | "singles" = "doubles") =>
    call<PlayerProfileReport>(`/api/players/${playerId}/profile?gameType=${gameType}`),

  /** BR-PAIR-1 — either order returns the same partnership. */
  /** FR-ADMIN-11 — everything waiting on an admin, in one request. */
  attention: () => call<AttentionReport>("/api/admin/attention"),

  pairProfile: (a: string, b: string) =>
    call<PairProfileReport>(`/api/pairs/${a}/${b}/profile`),

  audit: (params: { sessionId?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.sessionId) q.set("sessionId", params.sessionId);
    if (params.limit) q.set("limit", String(params.limit));
    return call<{ entries: AuditEntry[] }>(`/api/audit?${q.toString()}`);
  },

  preflight: (sessionId: string, unsyncedOps: number) =>
    call<Preflight>(`/api/sessions/${sessionId}/preflight?unsyncedOps=${String(unsyncedOps)}`),

  commit: (sessionId: string, body: { unsyncedOps?: number; overrideReason?: string }) =>
    post<CommitResult>(`/api/sessions/${sessionId}/commit`, body),

  uncommit: (sessionId: string, reason: string) =>
    post<void>(`/api/sessions/${sessionId}/uncommit`, { reason }),

  resolveDuplicate: (
    candidateId: string,
    body: { resolution: string; corrected?: { sideAScore: number; sideBScore: number } },
  ) => post<unknown>(`/api/duplicates/${candidateId}/resolve`, body),

  resolveConflict: (
    conflictId: string,
    body: {
      choice: string;
      expectedVersion: number;
      corrected?: { sideAScore: number; sideBScore: number };
    },
  ) => post<unknown>(`/api/conflicts/${conflictId}/resolve`, body),

  lateArrival: (gameId: string, body: { decision: "incorporate" | "reject"; reason?: string }) =>
    post<void>(`/api/games/${gameId}/late-arrival`, body),
};

/**
 * The idempotency key. Generated on the device before the request goes out, so
 * a retry after a dropped connection carries the same key and cannot create a
 * second game (ENTRY-11..14).
 */
export function newClientUuid(): string {
  return crypto.randomUUID();
}
